import { useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useEventListener, useOnClickOutside } from 'usehooks-ts';
import Flyout from '@/components/common/Flyout';
import { FlyoutMenuItem } from '@/components/common/Flyout/item';
import MenuItem from '@/components/conversations/conversation-menu-modal/menu-item';
import useChangeGroupMemberRole from '@/hooks/groups/use-change-group-member-role';
import useGroupInfo from '@/hooks/groups/use-group-info';
import useTransferGroup from '@/hooks/groups/use-transfer-group';
import { useIsDesktop } from '@/hooks/use-user-platform';
import type { GroupMember } from '@/models/group/group-info';
import { useAuthStore } from '@/stores/auth';

interface Props {
  show: boolean;
  groupId: number;
  position?: [number, number] | null | undefined;
  selectedMember: GroupMember | null;
  onClose: () => void;
}

export function GroupAdminMenu({ show, groupId, selectedMember, position, onClose }: Props) {
  const { t } = useTranslation();
  const ref = useRef<HTMLDivElement>(null);
  const userId = useAuthStore((s) => s.auth?.UserID);
  const { mutateAsync: changeGroupMemberRole } = useChangeGroupMemberRole(groupId);
  const { mutateAsync: transferGroup } = useTransferGroup(groupId);
  const { isAdmin } = useGroupInfo(groupId);
  const isDesktop = useIsDesktop();

  useOnClickOutside(ref, () => onClose());
  useEventListener('wheel', () => onClose());
  useEventListener('resize', () => onClose());

  if (!selectedMember) return null;

  if (isDesktop && position) {
    const [, posY] = position;

    return (
      <div
        ref={ref}
        className='absolute z-50 right-8 translate-y-full'
        style={{ top: posY }}>
        <div className='flex-col flex rounded-lg bg-neutral-100 min-w-40 p-2 shadow-xl z-20'>
          {selectedMember?.Role === 9 ? (
            <>
              <MenuItem
                label={t('conversation.setting.group.admin.to-member')}
                onClick={async () => {
                  await changeGroupMemberRole({ userId: selectedMember.UserID, role: 0 });
                  onClose();
                }}
              />

              <MenuItem
                label={t('conversation.setting.group.admin.transfer-owner')}
                onClick={async () => {
                  await transferGroup(selectedMember.UserID);
                  onClose();
                }}
              />
            </>
          ) : selectedMember.Role === 0 ? (
            <MenuItem
              label={t('conversation.setting.group.admin.to-admin')}
              onClick={async () => {
                await changeGroupMemberRole({ userId: selectedMember.UserID, role: 9 });
                onClose();
              }}
            />
          ) : null}
        </div>
      </div>
    );
  }

  return (
    <Flyout
      show={show}
      onClose={onClose}>
      <div className='flex flex-col items-stretch gap-2 font-normal'>
        {userId === selectedMember.UserID ? (
          <FlyoutMenuItem
            className='text-error'
            label={t('conversation.setting.group.admin.operate-self')}
          />
        ) : selectedMember?.Role === 9 ? (
          <div className='join join-vertical gap-y-px rounded-system-button'>
            <FlyoutMenuItem
              joined={isAdmin}
              label={t('conversation.setting.group.admin.to-member')}
              onClick={() => changeGroupMemberRole({ userId: selectedMember.UserID, role: 0 })}
            />

            <FlyoutMenuItem
              joined
              hidden={!isAdmin}
              label={t('conversation.setting.group.admin.transfer-owner')}
              onClick={() => transferGroup(selectedMember.UserID)}
            />
          </div>
        ) : selectedMember.Role === 10 ? (
          <FlyoutMenuItem
            className='text-error'
            label={t('conversation.setting.group.admin.operate-owner')}
          />
        ) : selectedMember.Role === 0 ? (
          <FlyoutMenuItem
            label={t('conversation.setting.group.admin.to-admin')}
            onClick={() => changeGroupMemberRole({ userId: selectedMember.UserID, role: 9 })}
          />
        ) : null}

        <FlyoutMenuItem
          className='mt-2 text-base font-semibold text-[#007AFF]'
          label={t('common.cancel')}
          onClick={onClose}
        />
      </div>
    </Flyout>
  );
}
